import React, { forwardRef, useEffect } from 'react'
import { Beta } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { paths } from '@mc/paths'
import { type CourseTile, GlobalCarousel } from 'components/GlobalCarousel'
import { TRENDING as TRENDING_SLUG } from 'libs/utils/categories'
import { ConcreteCategory } from './dataFetching'
import { trackSeeAllClick } from './events'

type InstructorCarouselProps = {
  loading: boolean
  category: ConcreteCategory | undefined
  courseTiles: CourseTile[]
  openNewPage?: boolean
  prioritizeTiles?: boolean
}

export const CourseTileCarousel = ({
  loading,
  category,
  courseTiles,
  openNewPage,
  prioritizeTiles,
}: InstructorCarouselProps) => {
  const { t } = useTranslation('@mc/next')

  const categorySlug = category?.slug ?? TRENDING_SLUG
  const heading = t(`home.catalogExplorer.subheadings.${categorySlug}`)
  const categoryName = category?.name ?? t('home.catalogExplorer.trending')
  const label = t('home.catalogExplorer.a11y.coursesLabel', {
    category: categoryName,
  })

  const seeAllHref = paths.category(categorySlug)

  return (
    <GlobalCarousel
      key={categorySlug}
      carouselSlug={categorySlug}
      watchTrailer={false}
      loading={loading}
      title={<CarouselHeading text={heading} seeAllHref={seeAllHref} />}
      srLabel={label}
      tiles={courseTiles}
      openNewPage={openNewPage}
      prioritizeTiles={prioritizeTiles}
      location='category-exploration'
    />
  )
}

type CarouselProps = {
  slug: string
  heading: string
  label: string
  tiles: CourseTile[]
  loading?: boolean
  openNewPage?: boolean
}

const CarouselResetPage = (
  { slug }: Pick<CarouselProps, 'slug'>,
  _: React.Ref<unknown>,
) => {
  const { container } = Beta.useCarousel()

  useEffect(() => {
    // go back to first page when the slug changes
    container?.scroll({
      left: 0,
      behavior: 'auto',
    })
  }, [slug, container])

  return null
}

export const CarouselHook = forwardRef(CarouselResetPage)
CarouselHook.displayName = 'CarouselHook'

const CarouselHeading = ({
  text,
  seeAllHref,
}: {
  text: string
  seeAllHref?: string
}) => {
  const { t } = useTranslation('@mc/next')

  return (
    <>
      <h3 className='mc-text-h6 d-inline mc-mr-3'>{text}</h3>
      {seeAllHref && (
        <a
          className='mc-text--normal mc-opacity--muted'
          href={seeAllHref}
          target='_blank'
          rel='noreferrer'
          onClick={trackSeeAllClick}
        >
          {t('home.catalogExplorer.seeAll')}
        </a>
      )}
    </>
  )
}
